import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Heart, 
  TrendingUp, 
  TrendingDown,
  Users,
  GraduationCap,
  AlertTriangle,
  CheckCircle,
  Quote,
  Target,
  BookOpen,
  DollarSign,
  Briefcase,
  Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { testimonials } from '../data/mock';

const Impact = () => {
  const challengeStats = [
    { value: '19%', label: 'St. Louis students proficient in reading', icon: BookOpen, color: 'red' },
    { value: '12%', label: 'St. Louis students proficient in math', icon: TrendingDown, color: 'red' },
    { value: '~70%', label: 'St. Louis graduation rate', icon: GraduationCap, color: 'amber' },
    { value: '87%', label: 'National graduation rate', icon: GraduationCap, color: 'green' },
  ];

  const economicStats = [
    { value: '$2.2T', label: 'Annual cost of low literacy to U.S. economy', icon: DollarSign },
    { value: '43%', label: 'Adults with lowest literacy living in poverty', icon: Home },
    { value: '$260K', label: 'Lifetime earnings gap: dropouts vs. graduates', icon: Briefcase },
    { value: '19%', label: 'Unemployment rate for recent high school dropouts', icon: Users },
  ];

  return (
    <div>
      <SEO 
        title="Our Impact - Education Results"
        description="See Mentors in Motion's impact in St. Louis. Our tutoring and mentoring programs improve literacy and graduation rates for youth."
        keywords="youth mentoring impact, education statistics St. Louis, nonprofit results, tutoring outcomes, graduation rates Missouri"
        canonicalUrl="/impact"
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.3),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              Our Impact
            </h1>
            <p className="text-base text-gray-300 leading-relaxed">
              In a city where less than one in five students is proficient in reading or math, programs like Mentors in Motion are not "extras", they are urgent interventions that change trajectories. When we help a student improve in reading and math, attend school more consistently, and stay engaged through mentoring and creative learning, we are building the foundation for their future success.
            </p>
          </div>
        </div>
      </section>

      {/* The Challenge Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              St. Louis Youth Are Being Left Behind
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              In St. Louis City, too many students are being asked to compete in a 21st-century economy without 21st-century skills.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {challengeStats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <Card key={index} className="border-0 shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    <div className={`${stat.color === 'red' ? 'bg-red-600' : stat.color === 'amber' ? 'bg-amber-500' : 'bg-green-600'} text-white p-6 text-center`}>
                      <IconComponent className="w-8 h-8 mx-auto mb-2 opacity-80" />
                      <div className="text-4xl font-bold">{stat.value}</div>
                    </div>
                    <div className="p-4">
                      <p className="text-gray-600 text-center text-sm">
                        {stat.label}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed mb-4">
              According to state test data, only about <strong className="text-red-600">19% of students</strong> in Saint Louis Public Schools are proficient in reading and just <strong className="text-red-600">12% are proficient in math</strong>. That means <strong>more than four out of five students are below grade level</strong> in the very subjects that predict success in high school, college, and the workforce.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By comparison, on the national "Nation's Report Card," roughly one-third of U.S. 4th-graders are proficient in reading and more than one-third are proficient in math. Even in a country where overall scores have declined, St. Louis students are starting significantly further behind.
            </p>
          </div>
        </div>
      </section>

      {/* Literacy & Graduation Gaps */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Literacy & Math Gaps Become Graduation Gaps
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                These academic gaps don't stay on test reports, they show up in graduation numbers. While the national high school graduation rate is about <strong className="text-green-400">87%</strong>, the average graduation rate in Saint Louis Public Schools is closer to <strong className="text-red-400">70%</strong>.
              </p>
              <p className="text-gray-300 leading-relaxed mb-8">
                That gap represents <strong>thousands of young people</strong> entering adulthood without a diploma.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h4 className="font-semibold text-white mb-4">Research is Clear:</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-200">Students not reading proficiently by end of 3rd grade are <strong>4x more likely</strong> to leave high school without a diploma</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-200">Among the lowest readers, nearly <strong>1 in 4 fail to graduate</strong> on time</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl">
                <h3 className="text-xl font-bold mb-6 text-center">Graduation Rate Comparison</h3>
                <div className="space-y-8">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">National Average</span>
                      <span className="text-green-400 font-bold">87%</span>
                    </div>
                    <div className="bg-white/10 rounded-full h-4 overflow-hidden">
                      <div className="bg-green-500 h-full rounded-full" style={{ width: '87%' }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300">St. Louis Public Schools</span>
                      <span className="text-red-400 font-bold">~70%</span>
                    </div>
                    <div className="bg-white/10 rounded-full h-4 overflow-hidden">
                      <div className="bg-red-500 h-full rounded-full" style={{ width: '70%' }} />
                    </div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-6 text-center italic">
                  When a whole community's children are behind in literacy and math, it isn't just a school problem, it becomes a graduation crisis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Economic Cost Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              The Economic Cost of Low Literacy and Math Skills
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Falling behind in reading and math doesn't just limit academic options; it carries a lifetime economic cost.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {economicStats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-red-600" />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                    <p className="text-gray-600 text-sm">{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="bg-gray-50 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">National studies estimate that low adult literacy costs the U.S. economy up to <strong>$2.2 trillion every year</strong> in lost productivity and earnings.</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Home className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">About <strong>one-third of adults with low literacy are unemployed</strong>, and <strong>43% of adults with the lowest literacy skills live in poverty</strong>.</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">High school dropouts earn about <strong>$260,000 less over their lifetime</strong> than graduates and face higher unemployment rates.</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-red-600" />
                </div>
                <p className="text-gray-700">The unemployment rate for recent high school dropouts is nearly <strong>19%</strong>, compared with <strong>12%</strong> for recent graduates enrolled in college.</p>
              </div>
            </div>
            <p className="text-gray-600 mt-6 pt-6 border-t border-gray-200 italic">
              For St. Louis, where many students are already starting behind, every percentage point of improvement in literacy and math is tied to real dollars in family income, local tax base, and long-term economic stability.
            </p>
          </div>
        </div>
      </section>

      {/* Why MiM Matters */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8">
              Why Mentors in Motion's Work Matters
            </h2>
            <p className="text-xl text-red-100 leading-relaxed mb-10">
              These statistics are the backdrop for Mentors in Motion's work. When we help a student improve in reading and math, attend school more consistently, and stay engaged through mentoring and creative learning:
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-4" />
                <p className="text-white">We are <strong>reducing their odds of dropping out</strong> and increasing their chances of graduating.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <TrendingUp className="w-10 h-10 text-green-400 mx-auto mb-4" />
                <p className="text-white">We are <strong>building skills</strong> that directly affect their future earnings and employment prospects.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <Users className="w-10 h-10 text-green-400 mx-auto mb-4" />
                <p className="text-white">We are <strong>strengthening the long-term health</strong> of St. Louis's neighborhoods and economy, one scholar at a time.</p>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-8">
              <p className="text-xl font-semibold">
                In a city where less than one in five students is proficient in reading or math, programs like Mentors in Motion are not "extras", they are <strong>urgent interventions that change trajectories</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
              Voices from Our Community
            </h2>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900">
              What They're Saying
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-red-200 mb-4" />
                  <p className="text-gray-600 leading-relaxed mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.author}</div>
                      <div className="text-sm text-gray-500 capitalize">{testimonial.type}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Help Us Change Trajectories
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-10">
            Your support helps us reach more students, provide more tutoring hours, and expand our creative learning programs. Together, we can transform these challenges into opportunities for St. Louis youth.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8">
                <Heart className="w-5 h-5 mr-2" />
                Support Our Mission
              </Button>
            </Link>
            <Link to="/thank-you">
              <Button size="lg" variant="outline" className="border-gray-300 hover:bg-gray-100 font-semibold px-8">
                See Our Supporters
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impact;
